/*
 * newsbot - Top Stories with expandable rows
 */

import { Fragment, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";

import { EmptyListState } from "@components/emptystates";
import { useTopStories, useStoryArticles, useFeedGroupCounts } from "@api/queries";
import { Story, Article } from "@/types";
import { formatRelativeTime, classNames } from "@utils";
import { getFaviconUrl } from "@utils/favicon";
import { ImportanceBadge } from "@components/stories/ImportanceBadge";
import { SourceFavicons } from "@components/stories/SourceFavicons";
import { defaultHoursForFeedGroup, getTimeRangeOptions, type TimeRangeOption } from "./operations";

function ArticleRow({ article }: { article: Article }) {
  const domain = (() => {
    try {
      const url = new URL(article.url);
      return url.hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  return (
    <tr className="bg-gray-50 dark:bg-gray-850">
      <td colSpan={4} className="pl-12 pr-5 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400 dark:text-gray-500">└─</span>
          {domain && (
            <img
              src={getFaviconUrl(domain)}
              alt=""
              className="w-4 h-4"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <span className="text-gray-500 dark:text-gray-400 text-xs min-w-[100px]">
            {domain}
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-xl"
          >
            {article.title}
          </a>
          {article.published_at && (
            <>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap">
                {formatRelativeTime(article.published_at)}
              </span>
            </>
          )}
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-auto"
            title="Open in new tab"
          >
            ↗
          </a>
        </div>
      </td>
    </tr>
  );
}

function ExpandedArticles({ storyId }: { storyId: string }) {
  const { data: articles, isLoading, isError } = useStoryArticles(storyId);

  if (isLoading) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-850">
        <td colSpan={4} className="pl-12 pr-5 py-2 text-sm text-gray-500">
          Loading articles...
        </td>
      </tr>
    );
  }

  if (isError) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-850">
        <td colSpan={4} className="pl-12 pr-5 py-2 text-sm text-red-600 dark:text-red-400" role="alert">
          Failed to load articles
        </td>
      </tr>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-850">
        <td colSpan={4} className="pl-12 pr-5 py-2 text-sm text-gray-500">
          No articles found
        </td>
      </tr>
    );
  }

  return (
    <>
      {articles.map((article) => (
        <ArticleRow key={article.id} article={article} />
      ))}
    </>
  );
}

function StoryRow({ story }: { story: Story }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-750"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-label={story.headline || "Untitled story"}
      >
        <td className="hidden sm:table-cell pl-5 pr-3 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <ChevronRightIcon
              className={classNames(
                "w-4 h-4 text-gray-400 transition-transform",
                isExpanded && "rotate-90"
              )}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatRelativeTime(story.first_seen_at)}
            </span>
          </div>
        </td>
        <td className="pl-4 sm:pl-3 px-3 py-3">
          <div className="flex items-center gap-2">
            <ChevronRightIcon
              className={classNames(
                "sm:hidden w-4 h-4 shrink-0 text-gray-400 transition-transform",
                isExpanded && "rotate-90"
              )}
            />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 sm:line-clamp-1">
              {story.headline || "Untitled"}
            </span>
          </div>
        </td>
        <td className="hidden sm:table-cell px-3 py-3 whitespace-nowrap">
          <SourceFavicons domains={story.source_domains} />
        </td>
        <td className="px-3 pr-5 py-3 whitespace-nowrap">
          <ImportanceBadge score={story.importance_score} />
        </td>
      </tr>
      {isExpanded && <ExpandedArticles storyId={story.id} />}
    </>
  );
}

function TimeRangeSelect({
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (value: number) => void;
  options: TimeRangeOption[];
}) {
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <ListboxButton className="relative w-auto min-w-[7rem] cursor-default rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-815 py-1.5 pl-3 pr-10 text-left text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <span className="block truncate">{selectedOption?.label}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-815 py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-300 dark:border-gray-700">
              {options.map((option) => (
                <ListboxOption
                  key={option.value}
                  className={({ active, selected }) =>
                    classNames(
                      selected
                        ? "font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-750"
                        : active
                          ? "bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          : "text-gray-700 dark:text-gray-300",
                      "relative cursor-default select-none py-2 pl-3 pr-9"
                    )
                  }
                  value={option.value}
                >
                  {({ selected }) => (
                    <>
                      <span className="block truncate">{option.label}</span>
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <CheckIcon className="h-5 w-5 text-blue-600 dark:text-blue-500" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}

export const ActivityTable = () => {
  const [timeRange, setTimeRange] = useState(24);
  const [feedGroup, setFeedGroup] = useState<string | null>(null);
  const { data: feedGroupCounts } = useFeedGroupCounts();
  const { isLoading, isError, data } = useTopStories(10, timeRange, feedGroup);
  const timeRangeOptions = getTimeRangeOptions(feedGroup);

  const selectFeedGroup = (slug: string | null) => {
    setFeedGroup(slug);
    setTimeRange(defaultHoursForFeedGroup(slug));
  };

  const feedGroupTabs = feedGroupCounts && (
    <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 mt-4 overflow-x-auto">
      <button
        onClick={() => selectFeedGroup(null)}
        className={classNames(
          "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
          !feedGroup
            ? "border-blue-500 text-blue-600 dark:text-blue-400"
            : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        )}
      >
        All ({feedGroupCounts.total})
      </button>
      {feedGroupCounts.groups.map((group) => (
        <button
          key={group.slug}
          onClick={() => selectFeedGroup(group.slug)}
          className={classNames(
            "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
            feedGroup === group.slug
              ? "border-blue-500 text-blue-600 dark:text-blue-400"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          )}
        >
          {group.name} ({group.story_count})
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col mt-12">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium leading-6 text-black dark:text-white">
          Top Stories
        </h3>
        <TimeRangeSelect value={timeRange} onChange={setTimeRange} options={timeRangeOptions} />
      </div>

      {feedGroupTabs}

      {isError ? (
        <div className="py-8 text-center text-red-500 dark:text-red-400" role="alert">
          Failed to load stories. Please refresh.
        </div>
      ) : isLoading ? (
        <div className="mt-4 animate-pulse">
          <EmptyListState text="Loading..." />
        </div>
      ) : !data || data.length === 0 ? (
        <div className="mt-4">
          <EmptyListState text="No top stories in this time range." />
        </div>
      ) : (
        <div className="w-full mt-4 mb-2">
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md overflow-auto">
            <table aria-label="Top Stories" className="w-full rounded-md divide-y divide-gray-200 dark:divide-gray-750">
              <thead className="bg-gray-100 dark:bg-gray-850">
                <tr>
                  <th scope="col" className="hidden sm:table-cell w-24 pl-5 pr-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400 rounded-tl-md">
                    Age
                  </th>
                  <th scope="col" className="pl-4 sm:pl-3 px-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400 sm:rounded-none rounded-tl-md">
                    Headline
                  </th>
                  <th scope="col" className="hidden sm:table-cell w-32 px-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                    Sources
                  </th>
                  <th scope="col" className="w-24 px-3 pr-5 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400 rounded-tr-md">
                    Importance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-150 dark:divide-gray-750">
                {data.map((story) => (
                  <StoryRow key={story.id} story={story} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="mt-2 text-right">
        <Link
          to="/stories"
          search={{
            page: 0,
            sort: "last_article_at",
            dir: "desc",
            q: "",
            minImportance: undefined,
            category: "",
            tags: "",
            vertical: feedGroup ?? "",
          }}
          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          View all stories
        </Link>
      </div>
    </div>
  );
};
